export interface ContentBlock {
  id: string;
  text: string;
  type: 'heading' | 'paragraph' | 'quote' | 'listitem';
  level?: number;
}

export type MarkdownSegment = {
  type: 'text' | 'bold' | 'italic';
  text: string;
  vStart: number;
  vEnd: number;
};

export interface HighlightNote {
  blockId: string;
  startIndex: number;
  endIndex: number;
}

export const parseMarkdownBlocks = (markdown: string): ContentBlock[] => {
  const lines = markdown.split('\n');
  const blocks: ContentBlock[] = [];
  let paragraphAcc: string[] = [];
  let blockIndex = 0;

  const flushParagraph = () => {
    const text = paragraphAcc.join('\n').trim();
    if (text) {
      blocks.push({ id: `block-${blockIndex}`, text, type: 'paragraph' });
      blockIndex += 1;
    }
    paragraphAcc = [];
  };

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/u);
    const quoteMatch = line.match(/^>\s*(.*)/u);
    const listMatch = line.match(/^[-*]\s+(.*)/u);

    if (/^[-*_]{3,}\s*$/u.test(line)) {
      flushParagraph();
    } else if (headingMatch) {
      const marker = headingMatch[1] ?? '';
      const text = headingMatch[2] ?? '';
      flushParagraph();
      blocks.push({
        id: `block-${blockIndex}`,
        level: marker.length,
        text,
        type: 'heading',
      });
      blockIndex += 1;
    } else if (quoteMatch) {
      flushParagraph();
      blocks.push({
        id: `block-${blockIndex}`,
        text: quoteMatch[1] ?? '',
        type: 'quote',
      });
      blockIndex += 1;
    } else if (listMatch) {
      flushParagraph();
      blocks.push({
        id: `block-${blockIndex}`,
        text: listMatch[1] ?? '',
        type: 'listitem',
      });
      blockIndex += 1;
    } else if (line.trim() === '') {
      flushParagraph();
    } else {
      paragraphAcc.push(line);
    }
  }

  flushParagraph();
  return blocks;
};

export const parseToVisibleSegments = (rawText: string): MarkdownSegment[] => {
  const segments: MarkdownSegment[] = [];
  let visiblePosition = 0;
  let lastRawIndex = 0;

  for (const match of rawText.matchAll(/\*\*(.+?)\*\*|\*([^*\n]+?)\*/gu)) {
    const [, boldText, italicText] = match;
    const rawIndex = match.index;
    if (rawIndex > lastRawIndex) {
      const text = rawText.slice(lastRawIndex, rawIndex);
      segments.push({
        text,
        type: 'text',
        vEnd: visiblePosition + text.length,
        vStart: visiblePosition,
      });
      visiblePosition += text.length;
    }

    let text: string;
    let type: MarkdownSegment['type'];
    if (boldText !== undefined) {
      text = boldText;
      type = 'bold';
    } else {
      text = italicText ?? '';
      type = 'italic';
    }
    segments.push({
      text,
      type,
      vEnd: visiblePosition + text.length,
      vStart: visiblePosition,
    });
    visiblePosition += text.length;
    lastRawIndex = rawIndex + match[0].length;
  }

  if (lastRawIndex < rawText.length) {
    const text = rawText.slice(lastRawIndex);
    segments.push({
      text,
      type: 'text',
      vEnd: visiblePosition + text.length,
      vStart: visiblePosition,
    });
  }

  return segments;
};

export const blockNum = (blockId: string): number => {
  const match = blockId.match(/(\d+)$/u);
  return match ? Number.parseInt(match[1] ?? '0', 10) : 0;
};

export const resolveHighlightBounds = (
  highlight: HighlightNote,
  blockId: string,
): { startIdx: number; endIdx: number } | null => {
  if (!highlight.blockId.includes(':')) {
    if (highlight.blockId !== blockId) {
      return null;
    }
    return { endIdx: highlight.endIndex, startIdx: highlight.startIndex };
  }

  const [startBlock, endBlock] = highlight.blockId.split(':');
  const currentBlockNumber = blockNum(blockId);
  const startBlockNumber = blockNum(startBlock ?? '');
  const endBlockNumber = blockNum(endBlock ?? '');

  if (
    currentBlockNumber < startBlockNumber ||
    currentBlockNumber > endBlockNumber
  ) {
    return null;
  }

  if (currentBlockNumber === startBlockNumber) {
    return { endIdx: Number.POSITIVE_INFINITY, startIdx: highlight.startIndex };
  }

  if (currentBlockNumber === endBlockNumber) {
    return { endIdx: highlight.endIndex, startIdx: 0 };
  }

  return { endIdx: Number.POSITIVE_INFINITY, startIdx: 0 };
};
